import type { PermissionKey } from "@/lib/permissions";
import { api } from "./client";

/**
 * Admin permissions — bella-admin-api `src/modules/admin-access/`.
 *
 * `me` says what the signed-in admin may do; the rest serves the Roles &
 * permissions screen. Types mirror the DTOs in
 * `src/modules/admin-access/dto/admin-access.dto.ts`. Saves send the complete
 * set, never a change, and name keys — never row ids.
 */

export interface MyPermissions {
  /** Owners hold every permission, always. */
  isOwner: boolean;
  permissions: PermissionKey[];
}

export interface CatalogueAction {
  key: PermissionKey;
  name: string;
}

export interface CatalogueScreen {
  key: PermissionKey;
  name: string;
  actions: CatalogueAction[];
}

export interface PermissionCatalogue {
  /** In display order. */
  screens: CatalogueScreen[];
  /** Keys the API checks but the database lacks — only Owners can use them. */
  missing: PermissionKey[];
}

export interface AdminRole {
  id: number;
  name: string;
  isOwner: boolean;
  activeAdmins: number;
}

export interface PermissionAdmin {
  id: number;
  name: string;
  email: string | null;
  roleId: number | null;
  roleName: string | null;
  isOwner: boolean;
  isActive: boolean;
}

export interface RoleGrants {
  role: AdminRole;
  allowed: PermissionKey[];
}

export type OverrideValue = "allow" | "deny";

export interface PermissionOverride {
  key: PermissionKey;
  value: OverrideValue;
}

export interface AdminGrants {
  admin: PermissionAdmin;
  /** What the admin's role grants, before overrides. */
  roleAllowed: PermissionKey[];
  overrides: PermissionOverride[];
  /** What the admin can actually do. */
  effective: PermissionKey[];
}

export const adminAccessKeys = {
  all: ["admin-access"] as const,
  me: () => [...adminAccessKeys.all, "me"] as const,
  catalogue: () => [...adminAccessKeys.all, "catalogue"] as const,
  roles: () => [...adminAccessKeys.all, "roles"] as const,
  role: (roleId: number) => [...adminAccessKeys.all, "role", roleId] as const,
  admins: () => [...adminAccessKeys.all, "admins"] as const,
  admin: (adminId: number) => [...adminAccessKeys.all, "admin", adminId] as const,
};

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export function getMyPermissions(signal?: AbortSignal): Promise<MyPermissions> {
  return api.get<MyPermissions>("/admin-access/me", withSignal(signal));
}

export function getPermissionCatalogue(
  signal?: AbortSignal,
): Promise<PermissionCatalogue> {
  return api.get<PermissionCatalogue>("/admin-access/catalogue", withSignal(signal));
}

export function listAdminRoles(signal?: AbortSignal): Promise<AdminRole[]> {
  return api.get<AdminRole[]>("/admin-access/roles", withSignal(signal));
}

export function getRoleGrants(roleId: number, signal?: AbortSignal): Promise<RoleGrants> {
  return api.get<RoleGrants>(`/admin-access/roles/${roleId}`, withSignal(signal));
}

export function saveRoleGrants(
  roleId: number,
  allowed: PermissionKey[],
): Promise<RoleGrants> {
  return api.put<RoleGrants>(`/admin-access/roles/${roleId}`, { allowed });
}

export function listPermissionAdmins(signal?: AbortSignal): Promise<PermissionAdmin[]> {
  return api.get<PermissionAdmin[]>("/admin-access/admins", withSignal(signal));
}

export function getAdminGrants(
  adminId: number,
  signal?: AbortSignal,
): Promise<AdminGrants> {
  return api.get<AdminGrants>(`/admin-access/admins/${adminId}`, withSignal(signal));
}

export function saveAdminOverrides(
  adminId: number,
  overrides: PermissionOverride[],
): Promise<AdminGrants> {
  return api.put<AdminGrants>(`/admin-access/admins/${adminId}`, { overrides });
}
